import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import CategoryTreeSelector from '../CategoryTreeSelector/CategoryTreeSelector';
import { categoryService } from '../../services/categoryService';
import { type VendorProfile, vendorProfileService } from '../../services/vendorProfileService';

interface CategoriesEditPopupProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedData: any) => void;
  vendorProfile: VendorProfile | null;
}

const CategoriesEditPopup: React.FC<CategoriesEditPopupProps> = ({
  open,
  onClose,
  onUpdate,
  vendorProfile
}) => {
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>('');
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with current vendor data
  useEffect(() => {
    const initializeForm = async () => {
      if (vendorProfile?.categoryTree) {
        // Set subcategories, types, and items from existing data
        setSelectedSubCategories(vendorProfile.categoryTree.subCategories || []);
        setSelectedTypes(vendorProfile.categoryTree.types || []);
        setSelectedItems(vendorProfile.categoryTree.items || []);
        
        // Find the correct category ID - check if mainCategory is already an ID or a name
        const mainCategoryValue = vendorProfile.categoryTree.mainCategory;
        if (mainCategoryValue) {
          try {
            const response = await categoryService.getAllCategories();
            if (response.message === 'Success' && response.data.categories) {
              console.log('Available categories:', response.data.categories.map(cat => ({ id: cat.categoryId, name: cat.name })));
              console.log('Looking for category:', mainCategoryValue);
              
              // Check if the stored value is already a category ID (UUID format)
              const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(mainCategoryValue);
              
              let category;
              if (isUUID) {
                // If it's a UUID, search by ID
                category = response.data.categories.find(
                  cat => cat.categoryId === mainCategoryValue && cat.isActive
                );
              } else {
                // If it's not a UUID, search by name
                category = response.data.categories.find(
                  cat => cat.name === mainCategoryValue && cat.isActive
                );
              }
              
              if (category) {
                console.log('Found matching category:', category.categoryId);
                setSelectedMainCategory(category.categoryId);
              } else {
                console.warn(`Category "${mainCategoryValue}" not found in available categories`);
                console.log('Available category names:', response.data.categories.map(cat => cat.name));
                // Don't set a fallback - let the user select from available options
                setSelectedMainCategory('');
              }
            }
          } catch (error) {
            console.error('Error fetching categories for mapping:', error);
            // Don't set a fallback - let the user select from available options
            setSelectedMainCategory('');
          }
        }
      }
    };

    if (open && vendorProfile) {
      initializeForm();
    }
  }, [vendorProfile, open]);

  const handleSubmit = async () => {
    // Validate selections
    if (!selectedMainCategory) {
      toast.error('Please select a main category');
      return;
    }

    if (selectedSubCategories.length === 0) {
      toast.error('Please select at least one sub category');
      return;
    }

    if (selectedTypes.length === 0) {
      toast.error('Please select at least one type');
      return;
    }

    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    if (!vendorProfile) {
      toast.error('No vendor session found. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Call API to save category tree
      const response = await vendorProfileService.editProfile(vendorProfile.vendorId, {
        categoryTree: {
          mainCategory: selectedMainCategory,
          subCategories: selectedSubCategories,
          types: selectedTypes,
          items: selectedItems,
        }
      });

      if (response.success) {
        toast.success('Categories updated successfully!');
        // Keep keys/IDs in local state (same as API). Display layer resolves names.
        onUpdate({
          categoryTree: {
            mainCategory: selectedMainCategory,
            subCategories: selectedSubCategories,
            types: selectedTypes,
            items: selectedItems,
          }
        });
        handleClose();
      } else {
        toast.error(response.message || 'Failed to update categories');
      }
    } catch (error: any) {
      console.error('Error updating categories:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to update categories. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography sx={{ 
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 700,
          fontSize: '20px',
          color: '#181818'
        }}>
          Edit Service Categories & Items
        </Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          <CategoryTreeSelector
            selectedMainCategory={selectedMainCategory}
            selectedSubCategories={selectedSubCategories}
            selectedTypes={selectedTypes}
            selectedItems={selectedItems}
            onMainCategoryChange={setSelectedMainCategory}
            onSubCategoryChange={setSelectedSubCategories}
            onTypeChange={setSelectedTypes}
            onItemChange={setSelectedItems}
            showMainCategory={true}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%'
          }}
        >
          {/* Update Button */}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: '#67314f',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5a2a42'
              },
              '&:disabled': {
                backgroundColor: '#93989c',
                color: 'white'
              }
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                Updating...
              </Box>
            ) : (
              'Update Categories'
            )}
          </Button>

          {/* Cancel Button */}
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: 'transparent',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: '#67314f',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CategoriesEditPopup;
